"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Camera, Clock, Sun, Aperture, Maximize2 } from "lucide-react";
const fragments = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/vintage_portrait_bride_laughing.png",
  alt: "Sposa che ride durante il ritratto",
  note: "Sviluppo: 45s, Luce Naturale",
  size: "large",
  icon: Sun
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/retro_camera_lens_detail.png",
  alt: "Dettaglio lente fotocamera artigianale",
  note: "Obiettivo: 50mm f/1.8, 1940",
  size: "small",
  icon: Aperture
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/couple_holding_hands_bw.png",
  alt: "Coppia che si tiene per mano",
  note: "Esposizione: 1/60s, Carta Baritata",
  size: "medium",
  icon: Clock
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/child_smiling_vintage_hat.png",
  alt: "Bambino con cappello vintage",
  note: "Sviluppo: 60s, Ombra Parziale",
  size: "small",
  icon: Sun
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/elderly_couple_dancing.png",
  alt: "Coppia anziana che balla",
  note: "Scatto: Istantaneo, Focale 35mm",
  size: "wide",
  icon: Camera
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/wedding_toast_glasses.png",
  alt: "Brindisi di matrimonio",
  note: "Contrasto: Alto, Sviluppo Rapido",
  size: "small",
  icon: Clock
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/musician_playing_sax.png",
  alt: "Musicista che suona il sassofono",
  note: "Luce: Diretta, Grana Fine",
  size: "tall",
  icon: Sun
}, {
  id: 8,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/group_friends_laughing_party.png",
  alt: "Gruppo di amici che ride alla festa",
  note: "Tempo: 1/125s, Carta Opaca",
  size: "large",
  icon: Aperture
}, {
  id: 9,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/vintage_bicycle_flowers.png",
  alt: "Bicicletta vintage con fiori",
  note: "Soggetto: Statico, Luce Diffusa",
  size: "medium",
  icon: Camera
}];
const BlurImage = ({
  src,
  alt,
  className,
  tilt
}: {
  src: string;
  alt: string;
  className?: string;
  tilt?: boolean;
}) => {
  const [loaded, setLoaded] = useState(false);
  return <div className={cn("relative overflow-hidden rounded-sm bg-muted transition-all duration-700 ease-out", tilt ? "rotate-2 hover:rotate-0 hover:scale-105 z-10 shadow-xl" : "hover:scale-[1.02]", className)}>
      <img src={src} alt={alt} loading="lazy" onLoad={() => setLoaded(true)} className={cn("h-full w-full object-cover transition-all duration-1000 ease-in-out", loaded ? "blur-0 scale-100 opacity-100" : "blur-xl scale-110 opacity-0")} />
      {}
      <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-300" />
    </div>;
};
export default function FragmentsOfHistory() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="frammenti-di-storia" ref={ref} className={cn("dark bg-background text-foreground py-20 px-4 md:px-8 overflow-hidden relative", isVisible ? "opacity-100" : "opacity-0 transition-opacity duration-1000")}>
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/noise_texture.png')] mix-blend-overlay" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-16 text-center space-y-4">
          <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight text-primary">Traces of Time</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-light">A mosaic of unique moments, captured with handcrafted cameras and developed in the light of day. Every portrait tells a story of authentic emotion.</p>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 auto-rows-[200px]">
          <TooltipProvider delayDuration={100}>
            {fragments.map((item, i) => {
            let spanClass = "";
            switch (item.size) {
              case "large":
                spanClass = "md:col-span-2 md:row-span-2";
                break;
              case "wide":
                spanClass = "md:col-span-2 md:row-span-1";
                break;
              case "tall":
                spanClass = "md:col-span-1 md:row-span-2";
                break;
              case "medium":
                spanClass = "md:col-span-1 md:row-span-1";
                break;
              default:
                spanClass = "md:col-span-1 md:row-span-1";
            }
            const isTilted = (i + 1) % 3 === 0;
            const Icon = item.icon;
            return <div key={item.id} data-index={i} className={cn("group relative", spanClass)}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="h-full w-full cursor-crosshair">
                        <BlurImage src={item.src} alt={item.alt} tilt={isTilted} className="h-full w-full shadow-md" />
                        
                        {}
                        <div className="absolute bottom-2 right-2 md:hidden bg-black/50 text-white p-1 rounded-full backdrop-blur-sm">
                           <Icon className="w-3 h-3" />
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-card border-border text-card-foreground p-3 shadow-xl max-w-[200px] text-center font-mono text-xs tracking-wide uppercase">
                      <div className="flex flex-col items-center gap-2">
                        <Icon className="w-4 h-4 text-primary" />
                        <span className="font-semibold text-primary/80">
                          Nota Tecnica
                        </span>
                        <span className="text-muted-foreground">
                          {item.note}
                        </span>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>;
          })}
          </TooltipProvider>
        </div>

        <div className="mt-16 text-center">
          <Link to="/WeddingsPage">
            <span className="inline-flex items-center justify-center gap-2 px-8 py-3 text-sm font-medium tracking-widest uppercase border border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all duration-300 rounded-sm">Explore the Gallery</span>
          </Link>
        </div>
      </div>
    </section>;
}